/**
 * lib/compliance/demo-mask.ts
 *
 * Demo privacy mode: hides crew names, emails and phone numbers on screen so
 * the system can be shown to another airline. Keeps the first and last
 * VISIBLE_EDGE characters and replaces the middle:  "Priya Sharma" → "Pr****ma".
 *
 * Display only -- the store and the API responses still carry the real
 * values (search and matching keep working). Backend exports are untouched.
 */

const VISIBLE_EDGE = 2;
const MASK = '****';

export function maskMiddle(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '';
  if (v.length <= VISIBLE_EDGE * 2) return MASK;
  return `${v.slice(0, VISIBLE_EDGE)}${MASK}${v.slice(-VISIBLE_EDGE)}`;
}

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_PATTERN = /\+?\d[\d\s-]{6,}\d/g;
const MIN_NAME_TOKEN = 3;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Mask emails, phone numbers and the given crew names (full name and first
 * name) inside free text such as a message subject or body. */
export function maskText(text: string, crewNames: ReadonlyArray<string>): string {
  let out = text.replace(EMAIL_PATTERN, (m) => maskMiddle(m)).replace(PHONE_PATTERN, (m) => maskMiddle(m));
  const tokens = new Set<string>();
  for (const name of crewNames) {
    const full = name.trim();
    if (!full) continue;
    tokens.add(full);
    const first = full.split(/\s+/)[0] ?? '';
    if (first.length >= MIN_NAME_TOKEN) tokens.add(first);
  }
  // Longest first, so a full name is replaced before its first name.
  for (const token of [...tokens].sort((a, b) => b.length - a.length)) {
    out = out.replace(new RegExp(`\\b${escapeRegExp(token)}\\b`, 'g'), (m) => maskMiddle(m));
  }
  return out;
}

/** Swap the tenant's airline code for the demo code in flight numbers and
 * duty labels: "AI2105", "AI 101", "FLY AI101" → "RH2105", "RH 101",
 * "FLY RH101". Only where the code is followed by a flight number. */
export function swapAirlineCode(text: string, realCode: string | null | undefined, demoCode: string): string {
  const real = (realCode ?? '').trim();
  if (!real || real === demoCode) return text;
  return text.replace(new RegExp(`\\b${escapeRegExp(real)}(?=\\s?\\d)`, 'g'), demoCode);
}
